
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Services() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const processSteps = [
    {
      step: '01',
      title: t('services.evaluation'),
      description: t('services.evaluationDesc'),
      icon: 'ri-search-eye-line',
    },
    {
      step: '02',
      title: t('services.dueDiligence'),
      description: t('services.dueDiligenceDesc'),
      icon: 'ri-file-search-line',
    },
    {
      step: '03',
      title: t('services.activeManagement'),
      description: t('services.activeManagementDesc'),
      icon: 'ri-team-line',
    },
    {
      step: '04',
      title: t('services.valueCreation'),
      description: t('services.valueCreationDesc'),
      icon: 'ri-line-chart-line',
    },
  ];

  const corporateServices = [
    t('services.corporate1'),
    t('services.corporate2'),
    t('services.corporate3'),
    t('services.corporate4'),
  ];

  const entrepreneurServices = [
    t('services.entrepreneur1'),
    t('services.entrepreneur2'),
    t('services.entrepreneur3'),
    t('services.entrepreneur4'),
    t('services.entrepreneur5'),
  ];

  return (
    <section id="services" className="py-32 bg-white relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background Elements */}
      <div className={`absolute top-1/2 ${isRTL ? 'right-0' : 'left-0'} w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2`}></div>
      <div className={`absolute bottom-0 ${isRTL ? 'left-0' : 'right-0'} w-64 h-64 bg-navy/5 rounded-full blur-3xl`}></div>

      <div className="max-w-[1400px] mx-auto px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-px bg-gold"></div>
            <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('services.label')}</span>
            <div className="w-12 h-px bg-gold"></div>
          </div>
          <h2 className="font-serif text-5xl lg:text-6xl text-navy mb-6">
            {t('services.title1')} <span className="text-gold">{t('services.title2')}</span>
          </h2>
          <p className="text-lg text-charcoal-light max-w-2xl mx-auto">
            {t('services.description')}
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
          {processSteps.map((item, index) => (
            <div key={index} className="group relative">
              <div className="bg-cream rounded-2xl p-8 h-full border border-cream-dark hover:border-gold/30 hover:shadow-xl transition-all duration-500">
                {/* Step Number */}
                <div className={`absolute -top-4 ${isRTL ? 'right-8' : 'left-8'} bg-gradient-to-r from-gold to-gold-dark text-navy text-sm font-bold px-4 py-1 rounded-full`}>
                  {item.step}
                </div>

                {/* Icon */}
                <div className="w-16 h-16 bg-navy rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold transition-colors duration-300">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-navy mb-4">{item.title}</h3>
                <p className="text-charcoal-light leading-relaxed text-sm">{item.description}</p>

                {/* Decorative Line */}
                <div className={`mt-6 w-0 h-0.5 bg-gold group-hover:w-full transition-all duration-500 ${isRTL ? 'mr-auto' : 'ml-0'}`}></div>
              </div>

              {/* Connector Arrow */}
              {index < 3 && (
                <div className={`hidden lg:block absolute top-1/2 ${isRTL ? '-left-4' : '-right-4'} z-10`}>
                  <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line text-2xl text-gold/30`}></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Service for Companies */}
          <div className="group">
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-cream-dark hover:border-gold/20">
              {/* Image */}
              <div className="relative h-72 overflow-hidden">
                <img
                  src="/services11.jpeg"
                  alt={t('services.corporateServices')}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent"></div>
                
                {/* Badge */}
                <div className={`absolute top-6 ${isRTL ? 'right-6' : 'left-6'} bg-gold text-navy text-sm font-semibold px-4 py-2 rounded-full`}>
                  {t('services.forCompanies')}
                </div>

                {/* Title on Image */}
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-3xl font-serif text-white">{t('services.corporateServices')}</h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <p className="text-charcoal-light leading-relaxed mb-6">
                  {t('services.corporateDesc')}
                </p>

                <div className="space-y-4">
                  {corporateServices.map((item, index) => (
                    <div key={index} className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-6 h-6 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-gold text-sm"></i>
                      </div>
                      <span className="text-charcoal text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/services"
                  className={`mt-8 inline-flex items-center gap-2 text-gold font-semibold hover:gap-4 transition-all duration-300 cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  {t('services.learnMore')} <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line`}></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Service for Individuals */}
          <div className="group">
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-cream-dark hover:border-gold/20">
              {/* Image */}
              <div className="relative h-72 overflow-hidden">
                <img
                  src="/services2.webp"
                  alt={t('services.entrepreneurSupport')}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/40 to-transparent"></div>
                
                {/* Badge */}
                <div className={`absolute top-6 ${isRTL ? 'right-6' : 'left-6'} bg-gold text-navy text-sm font-semibold px-4 py-2 rounded-full`}>
                  {t('services.forIndividuals')}
                </div>

                {/* Title on Image */}
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-3xl font-serif text-white">{t('services.entrepreneurSupport')}</h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                <p className="text-charcoal-light leading-relaxed mb-6">
                  {t('services.entrepreneurDesc')}
                </p>

                <div className="space-y-4">
                  {entrepreneurServices.map((item, index) => (
                    <div key={index} className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-6 h-6 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-gold text-sm"></i>
                      </div>
                      <span className="text-charcoal text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/services"
                  className={`mt-8 inline-flex items-center gap-2 text-gold font-semibold hover:gap-4 transition-all duration-300 cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  {t('services.learnMore')} <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line`}></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
